import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import useAxios from "../../utils/useAxios";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useHistory, useLocation } from "react-router-dom";
import { Container } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import FeatherIcon from "feather-icons-react";
import { CustomModal } from "../../components/CustomModel/CustomModal";
import useModal from "../../hooks/useModal";
import ClubForm from "./ClubForm";
import MenuList from "../../components/MenuList";
import { getInitials, sports } from "../../constants";
import debounce from "lodash.debounce";
import { prefetchUrl } from "../../utils/tabPrefetch";
import useCompactToolbar from "../../hooks/useCompactToolbar";
import { useTheme } from "../../contexts/ThemeContext";
import { CompactTableRow } from "../../components/CompactTableRow";

const ClubsList = ({ is_authorized = true }) => {
    const { t } = useTranslation();
    const authedApi = useAxios();
    // useAxios attaches the logged-in user's JWT unconditionally, regardless
    // of which UI mode is being rendered - viewing the public "Перегляд"
    // list while logged in as an admin still sent that token, so the
    // backend recognized the request as that admin (not anonymous) and
    // scoped ?all=true down to just their own club instead of every club.
    // Plain axios sends no auth header, matching what a signed-out visitor
    // actually sees.
    const api = is_authorized ? authedApi : axios;
    const history = useHistory();
    const location = useLocation();
    const { modalIsOpen, setIsOpen, openModal } = useModal(false);
    const { ref, inView } = useInView({ threshold: 1 });
    const searchInputRef = useRef(null);
    const requestIdRef = useRef(0);
    // Compact Table is a user-facing-only density preference - an admin on
    // /admin/clubs still gets the regular card view regardless of the
    // stored setting, only seeing it once they switch to Public View for
    // this same list. See Header.js/BottomNav.js for the matching
    // toggle-visibility gate.
    const { compactTable: rawCompactTable } = useTheme();
    const compactTable = rawCompactTable && !is_authorized;

    const [clubs, setClubs] = useState([]);
    const [currentPage, setCurrentPage] = useState("");
    const [countClubs, setCountClubs] = useState();
    const [loader, setLoader] = useState(false);
    const [skeletonCards, setSkeletonCards] = useState(24);
    const [editingClub, setEditingClub] = useState(null);
    const [hasError, setHasError] = useState(false);
    // Uncontrolled search input (see below) has no state of its own to read
    // from elsewhere - tracked separately just so hasActiveSearch (further
    // down) can tell "there's a query in progress" apart from empty, same
    // reasoning as TournamentsList.js's own activeChips/isSearching check.
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        document.title = t("Clubs");
        resetAndFetch();
    }, []);

    // A Compact Table row (index.css .trow: 9px top/bottom padding + a 20px
    // avatar) runs ~40px tall, well under a normal card - the backend's own
    // default page size (10) that comfortably fills one screen of cards
    // left several screens' worth of empty space below a compact list,
    // forcing an extra scroll-triggered fetch (or two) just to see a full
    // viewport. Sizing the very first page off actual viewport height (with
    // a few extra rows so the sentinel isn't right at the fold) fills the
    // screen in one request instead; card view keeps the backend default.
    const compactPageSize = () => Math.max(10, Math.ceil(window.innerHeight / 40) + 5);

    const resetAndFetch = (query = "") => {
        requestIdRef.current += 1;
        setClubs([]);
        setSkeletonCards(24);
        setHasError(false);
        const params = new URLSearchParams();
        if (query && query.length >= 2) params.set("search", query);
        // public page should show clubs across all organizers, not just
        // whatever the endpoint defaults to for an unauthenticated request
        if (!is_authorized) params.set("all", "true");
        // most clubs with active/upcoming tournaments first - no-op until the
        // backend adds support (see backend-tasks/2026-08-21-clubs-ordering-
        // by-tournament-actuality.md), same as the -tournaments_count/
        // -updated_at attempts below used to be.
        params.set("ordering", "-latest_tournament_date");
        if (compactTable) params.set("limit", compactPageSize());
        setCurrentPage(`/api/v1.0/clubs/${params.toString() ? "?" + params.toString() : ""}`);
    };

    const debouncedSearch = useMemo(() => debounce((val) => resetAndFetch(val), 400), []);
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    useEffect(() => {
        if ((inView || clubs.length === 0) && currentPage && !loader && !hasError) {
            fetchClubs();
        }
    }, [inView, currentPage, loader, hasError]);

    const fetchClubs = async () => {
        const reqId = requestIdRef.current;
        setLoader(true);
        try {
            const response = await prefetchUrl(api, currentPage);
            if (reqId !== requestIdRef.current) return;

            const results = response.data.results || [];
            const totalCount = response.data.count || 0;

            if (!countClubs) {
                setCountClubs(totalCount);
            }

            setClubs(prev => {
                const existingIds = new Set(prev.map(c => c.id));
                const newItems = results.filter(c => !existingIds.has(c.id));
                return [...prev, ...newItems];
            });
            setCurrentPage(response.data.next ? response.data.next.replace(/^https?:\/\/[^/]+/, '') : null);

            const updatedCount = clubs.length + results.length;
            const remaining = totalCount - updatedCount;
            setSkeletonCards(remaining > 0 ? Math.min(24, remaining) : 0);
        } catch (error) {
            if (reqId !== requestIdRef.current) return;
            console.error(error);
            setHasError(true);
        } finally {
            if (reqId === requestIdRef.current) {
                setLoader(false);
            }
        }
    };

    const handleRetry = () => {
        setHasError(false);
        fetchClubs();
    };

    const handleCreateClick = () => {
        setEditingClub(null);
        openModal();
    };

    const handleItemClick = (club) => {
        const params = new URLSearchParams(location.search);
        params.set("club_id", club.id);
        history.push({ search: params.toString() });
    };

    const { toolbarSentinelRef, progress: toolbarProgress, activated: toolbarActivated, collapse: collapseToolbar } = useCompactToolbar({
        onExpand: () => {
            window.scrollTo(0, 0);
            searchInputRef.current?.focus();
        },
        onCreate: is_authorized ? handleCreateClick : null
    });

    // paddingBottom mirrors paddingTop's own 10*(1-progress) once the row
    // has been activated (tapped open at least once this mount) - 10px
    // while expanded, shrinking back to 0 as it scrolls away. Before
    // activation, progress is permanently pinned at 1 (see
    // useCompactToolbar - that's what keeps the row collapsed by default
    // regardless of scroll position), which would make this formula
    // collapse to a flat 0 the entire time the page is scrolled, not just
    // at the top. A flat 10px override for that one specific state gives
    // the page-top layout its breathing room without pinning a permanent
    // gap-colored strip under the header for the rest of the scroll (this
    // used to be a real bug: a "black bar" hanging under the header at
    // every scroll position, not just at the top).
    // With an active search the row stays fully expanded regardless of
    // scroll, instead of auto-collapsing away - same fix as
    // TournamentsList.js's own hasActiveSearch/effectiveToolbarProgress.
    const hasActiveSearch = searchQuery.trim().length > 0;
    const effectiveToolbarProgress = hasActiveSearch ? 0 : toolbarProgress;

    const toolbarStyle = {
        paddingTop: `${5 * (1 - effectiveToolbarProgress)}px`,
        paddingBottom: toolbarActivated ? `${10 * (1 - effectiveToolbarProgress)}px` : "10px"
    };
    const searchRowStyle = {
        maxHeight: `${60 * (1 - effectiveToolbarProgress)}px`,
        opacity: 1 - effectiveToolbarProgress,
        pointerEvents: effectiveToolbarProgress >= 0.99 ? "none" : "auto"
    };

    return (
        <Container className="page_container">
            <div ref={toolbarSentinelRef} style={{ height: "1px" }} />
            {/* Navigation */}
            <div className="sticky-toolbar" style={toolbarStyle}>
                <nav className="tabs-container toolbar-search-row" style={searchRowStyle}>
                    <ul className="tabs">
                        <li style={{ position: "relative", width: "100%" }}>
                            <FeatherIcon icon="search" size={16} className="search-icon" />
                            <input
                                ref={searchInputRef}
                                className="search"
                                type="text"
                                placeholder={t("Clubs search: name...")}
                                onKeyUp={e => { setSearchQuery(e.target.value); debouncedSearch(e.target.value); }}
                                onBlur={e => { if (!e.target.value) collapseToolbar(); }}
                                style={{ paddingLeft: "30px" }}
                            />
                        </li>
                    </ul>
                </nav>
            </div>

            {compactTable ? (
                <div className="trows">
                    {clubs.map((club, idx) => (
                        <CompactTableRow
                            key={club.id || idx}
                            variant="clubs"
                            zebra={idx % 2 === 1}
                            avatarSrc={club.image}
                            title={club.name}
                            onClick={() => handleItemClick(club)}
                            stats={[
                                { icon: "calendar", value: club.tournaments_count ?? "—" },
                                { icon: "users", value: club.players_count ?? "—" }
                            ]}
                            actions={is_authorized && (
                                <MenuList
                                    deleteEndpoint={`/api/v1.0/clubs/${club.id}`}
                                    onDeleted={() => setClubs(prev => prev.filter(c => c.id !== club.id))}
                                    successMessage="Club deleted successfully"
                                    errorMessage="Error deleting club"
                                    is_edit
                                    onEdit={() => {
                                        setEditingClub(club);
                                        openModal();
                                    }}
                                    editText={t("Edit club")}
                                    title={club.name}
                                    titleIcon="award"
                                    asModal
                                />
                            )}
                        />
                    ))}
                    {(loader || (currentPage && clubs.length === 0)) && skeletonCards > 0 && (
                        Array.from({ length: skeletonCards }).map((_, idx) => (
                            <div className="trow trow-clubs" key={`skel-club-${idx}`} style={{ pointerEvents: "none" }}>
                                <Skeleton circle width={20} height={20} />
                                <Skeleton width="60%" height={12} />
                                <Skeleton width="80%" height={11} />
                                <Skeleton width="80%" height={11} />
                                <span />
                            </div>
                        ))
                    )}
                </div>
            ) : (
            <div className="tournament-cards">
                {clubs.map((club, idx) => (
                    <div className="tournament-card" key={club.id || idx} onClick={() => handleItemClick(club)}>
                        {is_authorized && (
                            <div className="tournament-card-actions" onClick={e => e.stopPropagation()}>
                                <MenuList
                                    deleteEndpoint={`/api/v1.0/clubs/${club.id}`}
                                    onDeleted={() => setClubs(prev => prev.filter(c => c.id !== club.id))}
                                    successMessage="Club deleted successfully"
                                    errorMessage="Error deleting club"
                                    is_edit
                                    onEdit={() => {
                                        setEditingClub(club);
                                        openModal();
                                    }}
                                    editText={t("Edit club")}
                                    title={club.name}
                                    titleIcon="award"
                                    asModal
                                />
                            </div>
                        )}
                        <div className="tournament-card-top">
                            <div className="tournament-card-thumb">
                                {club.image
                                    ? <img src={club.image} alt="" />
                                    : <span style={{ fontSize: "16px", fontWeight: 600 }}>{getInitials(club.name)}</span>
                                }
                            </div>
                            <div className="tournament-card-titles">
                                <div className="tournament-card-title">{club.name}</div>
                                <div className="tournament-card-club">{t(sports.find(s => s.id === club.sport)?.name)}</div>
                                {(club.tournaments_count != null || club.players_count != null || club.locations_count != null) && (
                                    <div className="tournament-card-info">
                                        {[
                                            club.tournaments_count != null && (
                                                <span className="tournament-card-players" key="tournaments">
                                                    <FeatherIcon icon="calendar" size={11} />{club.tournaments_count}
                                                </span>
                                            ),
                                            club.players_count != null && (
                                                <span className="tournament-card-players" key="players">
                                                    <FeatherIcon icon="users" size={11} />{club.players_count}
                                                </span>
                                            ),
                                            club.locations_count != null && (
                                                <span className="tournament-card-players" key="locations">
                                                    <FeatherIcon icon="map-pin" size={11} />{club.locations_count}
                                                </span>
                                            )
                                        ].filter(Boolean).reduce((acc, el, i) => i === 0 ? [el] : [...acc, " · ", el], [])}
                                    </div>
                                )}
                            </div>
                        </div>
                        <details className="tournament-card-details">
                            <summary className="tournament-card-toggle" onClick={e => e.stopPropagation()}>
                                <svg className="tournament-card-toggle-arrow" height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                    <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
                                </svg>
                            </summary>
                            <div className="tournament-card-meta">
                                <table className="tournament_table">
                                    <tbody>
                                    <tr>
                                        <td>{t("Contacts")}</td>
                                        <td style={{ textAlign: "right" }}>
                                            {club.phone ? (
                                                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                                    <a href={`tel:${club.phone}`} title={club.phone} onClick={e => e.stopPropagation()}><FeatherIcon icon="phone" size={16} /></a>
                                                </div>
                                            ) : "—"}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>{t("Social")}</td>
                                        <td style={{ textAlign: "right" }}>
                                            {club.instagram || club.facebook ? (
                                                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                                    {club.instagram && <a href={club.instagram} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}><FeatherIcon icon="instagram" size={16} /></a>}
                                                    {club.facebook && <a href={club.facebook} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}><FeatherIcon icon="facebook" size={16} /></a>}
                                                </div>
                                            ) : "—"}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </details>
                    </div>
                ))}
                {(loader || (currentPage && clubs.length === 0)) && skeletonCards > 0 && Array.from({ length: skeletonCards }).map((_, idx) => (
                    <div className="tournament-card" key={`skel-club-${idx}`} style={{ pointerEvents: "none" }}>
                        <div className="tournament-card-top">
                            <div className="tournament-card-thumb" style={{ background: "transparent" }}>
                                <Skeleton circle width={52} height={52} />
                            </div>
                            <div className="tournament-card-titles" style={{ flex: 1, minWidth: 0 }}>
                                <div className="tournament-card-title"><Skeleton width="65%" height={16} /></div>
                                <div className="tournament-card-club"><Skeleton width="40%" height={13} style={{ marginTop: 4 }} /></div>
                                <div className="tournament-card-info"><Skeleton width="50%" height={11} style={{ marginTop: 4 }} /></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            )}

            {!loader && hasError && (
                <div className="not-found">
                    <h2><FeatherIcon icon="alert-triangle" size={106}/></h2>
                    <h4>{t("Something went wrong")}</h4>
                    <p>{t("Failed to load list. Please check your connection and try again.")}</p>
                    <a href="#" className="action-button btn-green" onClick={(e) => { e.preventDefault(); handleRetry(); }}>{t("Try again")}</a>
                </div>
            )}

            {is_authorized && (
                <CustomModal isOpen={modalIsOpen} onClose={() => setIsOpen(false)}>
                    <ClubForm
                        onClose={() => setIsOpen(false)}
                        clubId={editingClub?.id || null}
                        img={editingClub?.image || null}
                        width={editingClub?.width || null}
                        height={editingClub?.height || null}
                        onSuccess={(newItem) => {
                            if (!newItem?.id) {
                                setClubs(prev => prev.filter(club => club.id !== editingClub?.id));
                                setIsOpen(false);
                                return;
                            }
                            setClubs(prev => {
                                const index = prev.findIndex(club => club.id === newItem.id);
                                if (index !== -1) {
                                    const updated = [...prev];
                                    updated[index] = newItem;
                                    return updated;
                                } else {
                                    return [newItem, ...prev];
                                }
                            });
                            setIsOpen(false);
                        }}
                    />
                </CustomModal>
            )}
            <div ref={ref}></div>
        </Container>
    );
};

export default ClubsList;
